import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultEntitiesExportFieldNames, entitiesExportFields } from "~pages/Entities/EntitiesListPage/constants/exportFields.ts";
import useFetchEntities from "~pages/Entities/EntitiesListPage/hooks/useFetchEntites.ts";
import { useGetEntitiesTableColumns } from "~pages/Entities/EntitiesListPage/hooks/useGetEntitiesTableColumns.tsx";
import { useGetEntitiesTableFilters } from "~pages/Entities/EntitiesListPage/hooks/useGetEntitiesTableFilters.tsx";
import { useEntityService } from "~pages/Entities/hooks/useEntityService.ts";
import type { SearchFilterParams } from "~pages/Persons/PersonListPage/types/searchFilterParams.ts";
import { removeEmptyFields } from "~utils/removeEmptyFields.ts";

const EntitiesListPage = () => {
  const { sport, organizationId, customerId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { uiConfiguration } = useSportConfiguration();
  const location = useLocation();
  const dataTableUiConfiguration = uiConfiguration.pages.entities.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.entities.listPage.importConfig;
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const paginationConfig = useHandlePagination(50);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const columns = useGetEntitiesTableColumns();
  const filterConfig = useGetEntitiesTableFilters(
    removeEmptyFields({
      status: location.state?.status ? { value: location.state?.status, label: location.state?.status } : null,
    }),
  );

  const listParams = useMemo<SearchFilterParams>(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      nameFullLocalContains: searchText,
      sortBy: sortBy || "",
      gender: filterConfig.currentFilter.gender?.value,
      ageGroup: filterConfig.currentFilter.ageGroup?.value,
      discipline: filterConfig.currentFilter.discipline?.value,
      standard: filterConfig.currentFilter.standard?.value,
      status: filterConfig.currentFilter.status?.value,
      includeTotalCount: "true",
      include: ["entityGroups"],
    }),
    [paginationConfig, searchText, filterConfig.currentFilter, sortBy],
  );

  const { data, isLoading, refetch } = useFetchEntities(listParams, sortBy !== null);
  const { deleteEntity, isDeletePending, importEntities, exportEntities, isExportPending } = useEntityService();

  const totalCount = data?.totalCount || undefined;

  const getNavigateUrl = (entityId: string) => `/${customerId}/admin/${sport}/${organizationId}/entities/manage/${entityId}`;

  return (
    <DataTable
      title={formatMessage({ id: "entityListPage.title" })}
      columns={columns}
      handleRefresh={async () => {
        await refetch();
      }}
      filtersConfig={filterConfig}
      isDataLoading={isLoading}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => {
              void deleteEntity(row.entityId).then((deleted) => {
                if (deleted) {
                  void refetch();
                }
              });
            },
            loading: isDeletePending,
          },
          view: {
            onClick: (row) => navigate(getNavigateUrl(row.entityId)),
          },
        },
      }}
      handleAdd={() => navigate(`/${customerId}/admin/${sport}/${organizationId}/entities/manage`)}
      addButtonIntlKey={"entity"}
      rowNavigation={{
        getNavigateUrl: (row) => getNavigateUrl(row.entityId),
      }}
      rowKey={"entityId"}
      rowNameKey={"nameFullLocal"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.entities`}
      columnSortConfig={{
        sorterLocalStorageKey: `tables.settings.sort.entities`,
        defaultSorter: {
          columnKey: "nameFullLocal",
          order: "ascend",
        },
        onSortChange: setSortBy,
      }}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      exportConfig={{
        handleExport: exportEntities,
        loading: isExportPending,
        defaultFields: defaultEntitiesExportFieldNames,
        groupedFields: entitiesExportFields,
        localStorageKey: "export.persons",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: importEntities,
      }}
      permissionNames={[PermissionNames.ENTITY]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, total: totalCount }}
    />
  );
};

export default EntitiesListPage;
